import React, { useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import _ from 'lodash'

// Components
import { BadgePreview } from '../../components/BadgePreview'
import { Button } from '../../components/Button'
import { EventHeader } from '../../components/EventHeader'
import { StateContainer } from '../../components/StateContainer'
import { Tooltip } from '../../components/Tooltip'
import PreviewBadgeModal from '../EventBadgeBuilder/PreviewBadgeModal'

// Local Components
import { DesignSettings } from './DesignSettings'
import { KioskSettings } from './KioskSettings'
import { Popups } from './Popups'

// Assets
import Badger from '../../assets/images/badger_white.svg'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'

// Utils & Service
import {
  getBadgeImages,
  getGlobalFontsFromBadge,
  waitForGoogleFonts,
} from '../EventBadgeBuilder/helpers'
import { getBadge } from '../../services/badges.service'
import { addKioskConfiguration, getKioskConfiguration } from '../../services/kiosks.service'
import { joinClassNames } from '../../utils/helpers'

const TABS = [
  {
    id: 0,
    label: 'Design',
  },
  {
    id: 1,
    label: 'Kiosk Settings',
  },
  {
    id: 3,
    label: 'Pop-Ups',
  },
]

const DEFAULT_KIOSK_CONFIG = {
  useDefaultBrand: true,
  backgroundColor: '#ffffff',
  fontColor: '#000000',
  googleFont: null,
  customFont: null,
  headerImage: null,
  headerBase64: null,
  logoImage: null,
  logoBase64: null,
  footerImage: null,
  footerBase64: null,
  adminPassword: null,
  useVerification: false,
  verificationField: null,
  useGdprPopup: true,
  useExternalPopupField: false,
  externalPopupField: null,
}

const EventSettings = observer(() => {
  // Context
  const navigate = useNavigate()
  const { event, organizationId, setEvent } = useContext(NavigationStoreContext)

  // State
  const [active, setActive] = useState(TABS[0])
  const [kioskConfiguration, setKioskConfiguration] = useState(event.kioskConfiguration)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [selectedAttendee, setSelectedAttendee] = useState(null)
  const [badgeImages, setBadgeImages] = useState({})
  const [badgeConfiguration, setBadgeConfiguration] = useState(null)
  const [googleFonts, setGoogleFonts] = useState([])
  const [googleFontsLoaded, setGoogleFontsLoaded] = useState(false)

  const googleFontsKey = useMemo(
    () =>
      (googleFonts || [])
        .map((f) => f.font)
        .sort()
        .join(','),
    [googleFonts],
  )

  // Wait for Google fonts to load
  useEffect(() => {
    if (googleFonts && googleFonts.length > 0) {
      setGoogleFontsLoaded(false)
      waitForGoogleFonts(googleFonts).then(() => {
        setGoogleFontsLoaded(true)
      })
    }
  }, [googleFontsKey])

  useEffect(() => {
    if (!event.kioskConfiguration) {
      const createKioskConfiguration = async () => {
        const response = await addKioskConfiguration(
          event.id,
          DEFAULT_KIOSK_CONFIG,
          () => {},
          () => {},
        )

        if (response) {
          setKioskConfiguration(response)
          setEvent({ ...event, kioskConfiguration: response.id })
        }
      }

      createKioskConfiguration()
    } else {
      const getUpdatedData = async () => {
        const [kioskConfig, badgeConfig] = await Promise.all([
          getKioskConfiguration(
            event.id,
            event.kioskConfiguration,
            true,
            () => {},
            () => {},
          ),
          event.badgeConfiguration &&
            getBadge(
              event.id,
              event.badgeConfiguration,
              () => {},
              () => {},
            ),
        ])

        if (kioskConfig) {
          setKioskConfiguration(kioskConfig)
        }
        if (badgeConfig) {
          setBadgeConfiguration(badgeConfig)
        }
      }

      getUpdatedData()
    }
  }, [])

  useEffect(() => {
    if (badgeConfiguration) {
      const getImages = async () => {
        // Pull out all images from badge
        const imagesOnBadge = await getBadgeImages(badgeImages, badgeConfiguration.configObject)

        // Track badge images
        setBadgeImages(imagesOnBadge)
      }

      getImages()

      // Pre-load badge fonts
      const [badgeGoogleFonts] = getGlobalFontsFromBadge(
        badgeConfiguration.defaultFontGoogle || badgeConfiguration.defaultFontCustom,
        badgeConfiguration.configObject,
      )

      setGoogleFonts(_.uniq(badgeGoogleFonts))
      if (!badgeGoogleFonts.length) setGoogleFontsLoaded(true)
    }
  }, [badgeConfiguration])

  /**
   * Renders the active tab content.
   */
  const renderTabContent = () => {
    switch (active.id) {
      case 0:
        return (
          <DesignSettings
            event={event}
            kioskConfiguration={kioskConfiguration}
            setKioskConfiguration={setKioskConfiguration}
          />
        )
      case 1:
        return (
          <KioskSettings
            event={event}
            kioskConfiguration={kioskConfiguration}
            setKioskConfiguration={setKioskConfiguration}
          />
        )
      case 3:
        return (
          <Popups
            event={event}
            kioskConfiguration={kioskConfiguration}
            setKioskConfiguration={setKioskConfiguration}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="h-full w-full">
      <StateContainer>
        <div className="relative flex h-full w-full flex-col space-y-5 overflow-y-hidden py-3">
          <div className="flex flex-col items-start justify-between gap-1 px-3 lg:flex-row lg:items-center">
            <EventHeader event={event} />

            <div className="flex flex-row gap-2">
              {badgeConfiguration && (
                <Tooltip
                  content={
                    <div className="rounded-lg bg-white px-2 pb-0.5">
                      <span className="text-xs">Badge Configuration is deprecated.</span>
                    </div>
                  }
                  display={badgeConfiguration.usesDeprecatedConfig}
                  placement="left"
                >
                  <Button
                    label="Preview Attendee Badge"
                    onClick={() => setShowPreviewModal(true)}
                  />
                </Tooltip>
              )}
              <Tooltip
                content={
                  <div className="rounded-lg bg-white px-2 pb-0.5">
                    <span className="text-xs">Badge Configuration is deprecated.</span>
                  </div>
                }
                display={badgeConfiguration?.usesDeprecatedConfig || false}
                placement="left"
              >
                <Button
                  background="bg-purple border-purple hover:bg-purple-600"
                  disabled={
                    !badgeConfiguration || badgeConfiguration?.usesDeprecatedConfig || false
                  }
                  icon={<img src={Badger} alt="" className="h-5" />}
                  label="Launch Badgr"
                  onClick={() =>
                    navigate(`/organization/${organizationId}/event/${event.id}/kiosk`)
                  }
                />
              </Tooltip>
            </div>
          </div>

          <div className="h-full w-full">
            <div className="px-3">
              <nav className="flex" aria-label="Tabs">
                {TABS.map((tab) => {
                  const activeTab = active.id === tab.id

                  return (
                    <button
                      type="button"
                      key={tab.label}
                      onClick={() => setActive(tab)}
                      className={joinClassNames(
                        activeTab
                          ? 'border-purple-700 bg-[#8c56f626] text-purple-700'
                          : 'border-b-transparent text-gray-600 hover:border-purple-700 hover:bg-[#8c56f626] hover:text-purple-700',
                        'group relative flex w-1/3 items-center justify-center rounded-t-[10px] border-b-2 px-4 py-2 text-sm font-medium',
                      )}
                      aria-current={activeTab ? 'page' : undefined}
                    >
                      {tab.label}
                    </button>
                  )
                })}
              </nav>
            </div>

            <div className="shadow-xm flex h-[80%] w-full flex-col overflow-hidden rounded-[15px] bg-white px-5 py-4 tall:h-[90%]">
              {kioskConfiguration && renderTabContent()}
            </div>
          </div>
        </div>
      </StateContainer>

      {showPreviewModal && (
        <PreviewBadgeModal
          closeModal={() => setShowPreviewModal(false)}
          eventId={event.id}
          setSelectedAttendee={setSelectedAttendee}
        />
      )}

      {selectedAttendee && googleFontsLoaded && (
        <BadgePreview
          admissionItems={event.admissionItems}
          attendee={_.isArray(selectedAttendee) ? null : selectedAttendee}
          attendees={_.isArray(selectedAttendee) ? selectedAttendee : null}
          badgeImages={badgeImages}
          categories={event.attendeeCategories}
          config={badgeConfiguration}
          endPreview={() => setSelectedAttendee(null)}
          oilTypeOptions={event.oilTypeOptions}
          sessions={event.sessions}
          useConfirmationCode={event.useConfirmationAsId}
        />
      )}
    </div>
  )
})

export default EventSettings
